import { DOMAIN_NAME } from '@openx/components-form/validationRegexes';
import { createCpmFieldSchema } from '@openx/components-form/validationSchema/cpmFieldSchema';
import { createPercentFieldSchema } from '@openx/components-form/validationSchema/percentFieldSchema';
import type { TFunction } from 'i18next';
import { array, number, object, string } from 'yup';
import { getOrganizationsLabels, MAX_ORGANIZATION_LEVEL, organizationFields } from '../../../constants';
import type { OrganizationFormData } from '../../../types';

export const getValidationSchema = (t: TFunction) => {
  const labels = getOrganizationsLabels(t);

  return object().shape({
    [organizationFields.ALLOWED_COUNTRIES]: array(string()).min(1, t('At least one Allowed Country is required')),
    [organizationFields.ALLOWED_DOMAINS]: array()
      .of(string().matches(DOMAIN_NAME, t('Some of domains are not valid')))
      .min(1, t('At least one Allowed Domain is required')),
    [organizationFields.DEFAULT_CPM_CAP]: createCpmFieldSchema({
      field: organizationFields.DEFAULT_CPM_CAP,
      labels,
      t,
    }).test({
      message: t('{field} must be less than or equal to {max}', {
        field: labels[organizationFields.DEFAULT_CPM_CAP],
        max: labels[organizationFields.MAX_CPM_CAP],
      }),
      name: 'defaultFeeLessOrEqualToMax',
      test: (defaultCpmCap, ctx) => {
        const formData: OrganizationFormData = ctx.parent;
        const maxCpmCap = formData[organizationFields.MAX_CPM_CAP];

        return typeof defaultCpmCap !== 'number' || typeof maxCpmCap !== 'number' || defaultCpmCap <= maxCpmCap;
      },
    }),
    [organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED]: createPercentFieldSchema<OrganizationFormData>({
      field: organizationFields.DEFAULT_PLATFORM_SHARE_OX_SOURCED,
      labels,
      t,
    }),
    [organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED]: createPercentFieldSchema<OrganizationFormData>({
      field: organizationFields.DEFAULT_PLATFORM_SHARE_PARTNER_SOURCED,
      labels,
      t,
    }),
    [organizationFields.DEFAULT_SHARE]: createPercentFieldSchema<OrganizationFormData>({
      field: organizationFields.DEFAULT_SHARE,
      labels,
      t,
    }).test({
      message: t('{field} must be less than or equal to {max}', {
        field: labels[organizationFields.DEFAULT_SHARE],
        max: labels[organizationFields.MAX_SHARE],
      }),
      name: 'defaultFeeLessOrEqualToMax',
      test: (defaultShare, ctx) => {
        const formData: OrganizationFormData = ctx.parent;
        const maxShare = formData[organizationFields.MAX_SHARE];

        return typeof defaultShare !== 'number' || typeof maxShare !== 'number' || defaultShare <= maxShare;
      },
    }),
    [organizationFields.EXCHANGE_ACCOUNT_PARTNER_ID]: string().when(
      organizationFields.CURATED_DEAL_BUYER_DISCOUNTS_ENABLED,
      {
        is: true,
        then: schema => schema.required(t('Partner ID is required when Curated Deal Buyer Discounts are enabled')),
      }
    ),
    [organizationFields.MAX_CPM_CAP]: createCpmFieldSchema({ field: organizationFields.MAX_CPM_CAP, labels, t }).when(
      organizationFields.DEFAULT_CPM_CAP,
      {
        is: value => value !== null,
        then: schema =>
          schema.required(
            t('{field} is required when {dependent} is provided', {
              dependent: labels[organizationFields.DEFAULT_CPM_CAP],
              field: labels[organizationFields.MAX_CPM_CAP],
            })
          ),
      }
    ),
    [organizationFields.MAX_SHARE]: createPercentFieldSchema<OrganizationFormData>({
      field: organizationFields.MAX_SHARE,
      labels,
      t,
    }).when(organizationFields.DEFAULT_SHARE, {
      is: value => value !== null,
      then: schema =>
        schema.required(
          t('{field} is required when {dependent} is provided', {
            dependent: labels[organizationFields.DEFAULT_SHARE],
            field: labels[organizationFields.MAX_SHARE],
          })
        ),
    }),
    [organizationFields.NAME]: string().required(t('Name is required')),
    [organizationFields.PARENT_ACCOUNT]: object({
      id: string(),
      name: string(),
      depth: number(),
    })
      .nullable()
      .required(t('Parent Organization is required'))
      .test({
        message: t('Maximum organization level reached. You cannot add more sub-organizations.', {
          field: labels[organizationFields.PARENT_ACCOUNT],
        }),
        name: 'maxLevelReached',
        test: parentAccount => {
          return typeof parentAccount?.depth !== 'number' || parentAccount.depth < MAX_ORGANIZATION_LEVEL;
        },
      }),
  });
};
